import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@common/components/PropertiesLabel';
import { NumberInput } from '@common/components/inputs';
import { Alert, Stack } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPreferenceName } from '@/modules/userPreferences';

type Props = {
  value: number;
  recommendedSimulationTime: number;
  onChange: (duration: number) => void;
};

export function TimeForm({ value, recommendedSimulationTime, onChange }: Props) {
  const { t } = useTranslation('interface');
  const SIMULATION_DURATION_MAX = 24;

  const onDurationChange = useCallback(
    (hour: number) => {
      try {
        if (hour <= SIMULATION_DURATION_MAX) {
          onChange(hour);
        } else {
          onChange(SIMULATION_DURATION_MAX);
          throw new Error(
            t('interface:simulation.simulation_edit.time_form.max_duration', {
              hours: SIMULATION_DURATION_MAX,
            }),
          );
        }
      } catch (e) {
        return { error: e };
      }
    },
    [onChange, t],
  );

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_SIMULATION_EDIT_TIME}
      title={t('simulation.simulation_edit.time_form.header')}
      sx={{ px: 2 }}
    >
      <Stack gap={1}>
        <PropertiesLabel i18nkey='interface:simulation.simulation_edit.time_form.duration_input.label'>
          <NumberInput
            ariaLabel={t('simulation.simulation_edit.time_form.duration_input.label')}
            name='duration'
            value={value}
            onChange={onDurationChange}
            unit={t('simulation.simulation_edit.time_form.duration_input.unit')}
            sx={{ width: '100%' }}
            minValue={1}
            errorType='floating'
          />
        </PropertiesLabel>
        {recommendedSimulationTime > (value ?? 0) && (
          <Alert severity='info'>
            {t('simulation.simulation_edit.time_form.recommended_min_simulation_time', {
              amount: recommendedSimulationTime,
            })}
          </Alert>
        )}
      </Stack>
    </Accordion>
  );
}
